import { useEffect, useRef, useState } from "react";

export type BreakdownMode = "products" | "customers";

// SelectMenu keys options by a numeric `value`; map the two breakdowns to stable indices.
export const PRODUCTS_OPTION = 0;
export const CUSTOMERS_OPTION = 1;

// Once the in-content header (toggle + search) scrolls up to within this many px of the viewport top
// — i.e. under the nav row — the dock takes over. Tied to the header's real position (not a fixed
// page offset) because the KPI chart makes the header sit at a variable height.
const NAV_DOCK_TOP_OFFSET = 120;

interface AnalysisBreakdownOptions {
  defaultMode: BreakdownMode;
  /** Called when the toggle leaves the products breakdown, e.g. to drop the product drill. */
  onLeaveProducts: () => void;
  /** The two lists don't share a search term or filter — clear them so a stale query doesn't carry across. */
  resetListControls: () => void;
}

/**
 * Shared state behind the products/customers breakdown pages: the mode toggle plus the nav-dock
 * reveal (dock once the in-content header scrolls up under the nav row).
 */
export function useAnalysisBreakdown({ defaultMode, onLeaveProducts, resetListControls }: AnalysisBreakdownOptions) {
  const [mode, setMode] = useState<BreakdownMode>(defaultMode);

  const [docked, setDocked] = useState(false);
  // Keep the dock shown while its search is open, even if an empty result set collapses the scroll
  // back to the top (otherwise the search would vanish mid-typing).
  const [dockSearchOpen, setDockSearchOpen] = useState(false);
  // Marks the top of the in-content header; the active view renders it just above the list.
  const headerSentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onScroll = () => {
      const el = headerSentinelRef.current;
      setDocked(el != null && el.getBoundingClientRect().top < NAV_DOCK_TOP_OFFSET);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const switchMode = (next: BreakdownMode) => {
    if (next === mode) return;
    if (next === "customers") onLeaveProducts();
    resetListControls();
    setMode(next);
  };

  // Switching from the AnalysisNavDock's toggle also scrolls back to the top, which fades the dock
  // out and returns the user to the (now-switched) in-content header.
  const switchModeFromNavDock = (next: BreakdownMode) => {
    switchMode(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return { mode, switchMode, switchModeFromNavDock, docked, dockSearchOpen, setDockSearchOpen, headerSentinelRef };
}
